import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { resolvePlatformAdmin } from '@/lib/platform/usePlatformAdmin';
import { Loader2 } from 'lucide-react';

/**
 * Post-login dispatcher. Precedence:
 * 1. ?next= (non-/auth path) → honor it
 * 2. pulse_org_members admin → /app/admin
 * 3. is_platform_admin       → /app/platform/tenants
 * 4. first-time              → /app/welcome
 * 5. fallback                → /app/employee
 */
export function PostLoginRedirect() {
  const { user, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  // Safe next = in-app path. Allow /auth/claim (resumes claim flow); reject other /auth paths.
  const hasSafeNext = !!(
    next &&
    next.startsWith('/') &&
    (!next.startsWith('/auth') || next.startsWith('/auth/claim'))
  );

  const { data: pulseMembership, isLoading: pulseLoading } = useQuery({
    queryKey: ['post-login-pulse-check', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pulse_org_members')
        .select('role')
        .eq('user_id', user!.id)
        .order('role', { ascending: true }) // 'admin' sorts before 'employee'
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const isOrgAdmin = pulseMembership?.role === 'admin';

  const { data: platformAdmin, isLoading: platformLoading } = useQuery({
    queryKey: ['post-login-platform-check', user?.id],
    enabled: !!user?.id && !pulseLoading,
    queryFn: () => resolvePlatformAdmin(user!.id),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (authLoading || pulseLoading || platformLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  // 1. Honor ?next= if it's a safe in-app path (not back to /auth/*).
  if (hasSafeNext) {
    const platformOnlyAdmin = platformAdmin === true && !isOrgAdmin;
    const nextIsPlatform = next!.startsWith('/app/platform');
    const nextIsClaim = next!.startsWith('/auth/claim');
    if (platformOnlyAdmin && !nextIsPlatform && !nextIsClaim) {
      return <Navigate to="/app/platform/tenants" replace />;
    }
    return <Navigate to={next!} replace />;
  }

  // 2. Org admin (preserves current flow even for users who are also platform admin).
  if (isOrgAdmin) return <Navigate to="/app/admin" replace />;

  // 3. Platform-only admin.
  if (platformAdmin === true) return <Navigate to="/app/platform/tenants" replace />;

  // 4. First-time employee → welcome screen (one-shot, gated by localStorage).
  const seenWelcome = localStorage.getItem('pulse:welcomed') === '1';
  if (!seenWelcome) return <Navigate to="/app/welcome" replace />;

  // 5. Fallback.
  return <Navigate to="/app/employee" replace />;
}
